import { useState, useRef } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, FileText, Upload } from "lucide-react";
import { toast } from "sonner";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import {
  apiCreateDocumentTemplate,
  apiCreateDocumentTemplateFile,
  apiUpdateDocumentTemplate,
  type DocumentTemplate,
  type DocumentTemplateType,
} from "@/lib/api";

// ── Constants ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const TEMPLATE_TYPES: {
  value: DocumentTemplateType;
  labelKey: string;
}[] = [
  { value: "contract", labelKey: "documents.templateType.contract" },
  { value: "annex", labelKey: "documents.templateType.annex" },
  { value: "bank", labelKey: "documents.templateType.bank" },
  { value: "court", labelKey: "documents.templateType.court" },
  { value: "sick_leave", labelKey: "documents.templateType.sick_leave" },
  { value: "custom", labelKey: "documents.templateType.custom" },
];

const PLACEHOLDER_HINTS = [
  "{employee_name}",
  "{start_date}",
  "{position}",
  "{salary}",
];

// ── Props ─────────────────────────────────────────────────────────────────────

interface TemplateFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass an existing template to edit; omit for create */
  template?: DocumentTemplate | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function TemplateFormModal({
  open,
  onOpenChange,
  template,
}: TemplateFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!template;

  const [name, setName] = useState(template?.name ?? "");
  const [templateType, setTemplateType] = useState<DocumentTemplateType>(
    template?.templateType ?? "contract",
  );
  const [sourceType, setSourceType] = useState<"text" | "file">(
    template?.fileKey && !template?.contentText ? "file" : "text",
  );
  const [contentText, setContentText] = useState(template?.contentText ?? "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when modal opens fresh
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset only when closing
      setName(template?.name ?? "");
      setTemplateType(template?.templateType ?? "contract");
      setSourceType(template?.fileKey && !template?.contentText ? "file" : "text");
      setContentText(template?.contentText ?? "");
      setSelectedFile(null);
    }
    onOpenChange(open);
  };

  // ── Mutations ───────────────────────────────────────────────────────────────

  const createText = useMutation({
    mutationFn: () =>
      apiCreateDocumentTemplate({ name: name.trim(), templateType, contentText: contentText || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateCreated"));
      handleOpenChange(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const createFile = useMutation({
    mutationFn: () =>
      apiCreateDocumentTemplateFile(name.trim(), templateType, selectedFile!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateCreated"));
      handleOpenChange(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      apiUpdateDocumentTemplate(template!.id, {
        name: name.trim(),
        templateType,
        contentText: sourceType === "text" ? contentText : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document-templates"] });
      toast.success(t("documents.messages.templateUpdated"));
      handleOpenChange(false);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const isLoading =
    createText.isPending || createFile.isPending || updateMutation.isPending;

  const handleSave = () => {
    if (!name.trim()) {
      toast.error(t("documents.templates.validation.nameRequired"));
      return;
    }
    if (isEdit) {
      updateMutation.mutate();
    } else if (sourceType === "file") {
      if (!selectedFile) {
        toast.error(t("documents.templates.validation.fileRequired"));
        return;
      }
      createFile.mutate();
    } else {
      createText.mutate();
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {isEdit
              ? t("documents.templates.editTitle")
              : t("documents.templates.createTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {/* Name */}
          <FloatField
            label={t("documents.templates.nameLabel")}
            required
            value={name}
            onChange={(v) => setName(v as string)}
            placeholder={t("documents.templates.namePlaceholder")}
          />

          {/* Type */}
          <FloatWrapper label={t("documents.templates.typeLabel")} required>
            <Select
              value={templateType}
              onValueChange={(v) => setTemplateType(v as DocumentTemplateType)}
              disabled={isLoading}
            >
              <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_TYPES.map((templateOption) => (
                  <SelectItem key={templateOption.value} value={templateOption.value}>
                    {t(templateOption.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FloatWrapper>

          {/* Content type toggle — only for create */}
          {!isEdit && (
            <div className="mt-3 space-y-1.5">
              <p className="text-[13px] font-bold tracking-tight">{t("documents.templates.sourceLabel")}</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={sourceType === "text" ? "default" : "outline"}
                  onClick={() => setSourceType("text")}
                  disabled={isLoading}
                >
                  {t("documents.templates.sourceText")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={sourceType === "file" ? "default" : "outline"}
                  onClick={() => setSourceType("file")}
                  disabled={isLoading}
                >
                  <Upload className="h-3.5 w-3.5 mr-1" />
                  {t("documents.templates.sourceFile")}
                </Button>
              </div>
            </div>
          )}

          {/* Text content */}
          {(sourceType === "text" || isEdit) && (
            <div className="mt-3 space-y-1.5">
              <FloatField
                label={t("documents.templates.contentLabel")}
                type="textarea"
                value={contentText}
                onChange={(v) => setContentText(v as string)}
                placeholder={t("documents.templates.contentPlaceholder")}
                className="font-mono text-sm resize-y"
              />
              <p className="text-xs text-muted-foreground">
                <strong>{t("documents.templates.availablePlaceholders")}:</strong>{" "}
                {PLACEHOLDER_HINTS.map((p) => (
                  <code
                    key={p}
                    className="bg-muted px-1 py-0.5 rounded text-[11px] mr-1 cursor-pointer hover:bg-primary/10"
                    onClick={() => setContentText((prev) => prev + p)}
                  >
                    {p}
                  </code>
                ))}
              </p>
            </div>
          )}

          {/* File upload */}
          {!isEdit && sourceType === "file" && (
            <div className="space-y-1.5">
              <Label>{t("documents.templates.fileLabel")} *</Label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? (
                  <p className="text-sm font-medium text-foreground">
                    {selectedFile.name}
                  </p>
                ) : (
                  <div className="space-y-1">
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("documents.templates.filePlaceholder")}
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit
              ? t("documents.templates.saveChanges")
              : t("documents.templates.createAction")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
